import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import jsQR from "../vendor/jsQR";

const DeviceQrScanner = ({ onScan, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [manualMode, setManualMode] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const manualRef = useRef(null);

  useEffect(() => {
    if (manualMode) return undefined;
    let stopped = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setManualMode(true);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        });
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();

        const scan = () => {
          if (stopped) return;
          const canvas = canvasRef.current;
          const v = videoRef.current;
          if (v && canvas && v.readyState >= v.HAVE_ENOUGH_DATA) {
            canvas.width = v.videoWidth;
            canvas.height = v.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: "dontInvert",
            });
            if (code?.data) {
              stopped = true;
              stream.getTracks().forEach((t) => t.stop());
              onScan(code.data.trim());
              return;
            }
          }
          rafRef.current = requestAnimationFrame(scan);
        };
        rafRef.current = requestAnimationFrame(scan);
      } catch (err) {
        setCameraError("Kamera nem elerheto: " + err.message);
        setManualMode(true);
      }
    };

    start();
    return () => {
      stopped = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [onScan, manualMode]);

  const submitManual = () => {
    const val = manualRef.current?.value?.trim();
    if (val) onScan(val);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="text-white font-semibold text-sm">Olvasd be az olvaso QR kodjat</span>
        <button type="button" onClick={onClose} className="text-slate-400 p-1">
          <X size={22} />
        </button>
      </div>

      {!manualMode ? (
        <div className="flex-1 relative overflow-hidden">
          <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" />
          <canvas ref={canvasRef} className="hidden" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-52 h-52 border-2 border-white/60 rounded-xl" />
          </div>
          <button
            type="button"
            onClick={() => setManualMode(true)}
            className="absolute bottom-4 left-0 right-0 text-center text-slate-300 text-xs underline"
          >
            Kezi bevitel
          </button>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
          {cameraError && <p className="text-yellow-400 text-sm text-center">{cameraError}</p>}
          <p className="text-slate-300 text-sm text-center">Ird be a device MAC cimet:</p>
          <input
            ref={manualRef}
            type="text"
            placeholder="AA:BB:CC:DD:EE:FF"
            className="w-full max-w-xs bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm font-mono text-center"
            onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
          />
          <button type="button" className="bg-blue-600 text-white px-6 py-2 rounded text-sm" onClick={submitManual}>
            OK
          </button>
          {navigator.mediaDevices?.getUserMedia && (
            <button type="button" onClick={() => { setCameraError(null); setManualMode(false); }} className="text-slate-400 text-xs underline">
              Vissza a kamerahoz
            </button>
          )}
        </div>
      )}

      <p className="text-slate-400 text-xs text-center pb-5 px-4 pt-2">
        Tartsd a kamerat az eszkozon levo QR kod ele
      </p>
    </div>
  );
};

export default DeviceQrScanner;
